<template>
	<div class="relative w-full">
		<div
			v-if="hasMap && hasGallery"
			class="inline-flex mb-1 md:mb-0 md:absolute md:left-4 md:top-4 z-20"
		>
			<div class="relative flex p-1 md:p-1.5 bg-background-tertiary rounded-full select-none">
				<div
					class="absolute top-1.5 bottom-1.5 left-0 bg-white rounded-full shadow-xs transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
					:style="switchButtonsStyles"
				/>

				<button
					ref="galleryBtn"
					class="relative z-10 flex items-center justify-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-3 text-base font-bold transition-colors duration-300 focus:outline-hidden whitespace-nowrap shadow-green-sm text-body-primary"
					aria-label="Zobrazit galerii"
					@click="activeTab = 'gallery'"
				>
					<NuxtIcon name="custom/image" class="text-icon-primary text-lg md:text-2xl max-md:-mt-0.5" filled />
					Galerie
				</button>

				<button
					ref="mapBtn"
					class="relative z-10 flex items-center justify-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-3 text-base font-bold transition-colors duration-300 focus:outline-hidden whitespace-nowrap shadow-green-sm text-body-primary"
					aria-label="Zobrazit mapu"
					@click="activeTab = 'map'"
				>
					<NuxtIcon name="map" class="text-icon-primary text-lg md:text-2xl max-md:-mt-0.5" filled />
					Mapa
				</button>
			</div>
		</div>
		<ClientOnly>
			<Transition
				mode="out-in"
				:css="false"
				@enter="onEnter"
				@leave="onLeave"
			>
				<div
					v-if="activeTab === 'map' && hasMap"
					key="map"
				>
					<MapViewer
						:map="blok.map"
						class="rounded-2xl"
						tailwind-aspect-ratio="aspect-[328/336] sm:aspect-[751/519]"
					/>
				</div>
				<div
					v-else-if="activeTab === 'gallery' && hasGallery"
					key="gallery"
				>
					<RestaurantGallery :gallery="blok.gallery" />
				</div>
			</Transition>
			<template #fallback>
				<div class="rounded-2xl skeleton-gradient aspect-[328/336] sm:aspect-[751/519]" />
			</template>
		</ClientOnly>
	</div>
</template>

<script setup lang="ts">
	import {computed} from 'vue';
	import {useSessionStorage, useWindowSize} from '@vueuse/core';
	import {gsap} from 'gsap';
	import type {MapCarouselStoryblok} from '@/storyblok/types';

	type Props = {
		blok: MapCarouselStoryblok;
	};

	const props = defineProps<Props>();

	const hasMap = computed(() => !!props.blok.map?.filename);
	const hasGallery = computed(() => props.blok.gallery && props.blok.gallery.length > 0);

	const activeTab = useSessionStorage<'map' | 'gallery'>(
		`restaurant-tab-${props.blok._uid}`,
		hasGallery.value ? 'gallery' : 'map',
	);

	const galleryBtn = useTemplateRef<HTMLElement>('galleryBtn');
	const mapBtn = useTemplateRef<HTMLElement>('mapBtn');
	const {width: windowWidth} = useWindowSize();

	const switchButtonsStyles = computed(() => {
		const activeElement = activeTab.value === 'gallery' ? galleryBtn.value : mapBtn.value;

		if (!activeElement || windowWidth.value === 0) {
			return {opacity: 0};
		}

		return {
			opacity: 1,
			width: `${activeElement.offsetWidth}px`,
			transform: `translateX(${activeElement.offsetLeft}px)`,
		};
	});

	const onEnter = (el: Element, done: () => void) => {
		gsap.fromTo(el,
			{opacity: 0, y: 15, scale: 0.98},
			{opacity: 1, y: 0, scale: 1, duration: 0.4, ease: 'power3.out', clearProps: 'all', onComplete: done},
		);
	};

	const onLeave = (el: Element, done: () => void) => {
		gsap.fromTo(el,
			{opacity: 1, y: 0, scale: 1},
			{opacity: 0, y: -15, scale: 0.98, duration: 0.3, ease: 'power3.in', clearProps: 'all', onComplete: done},
		);
	};
</script>
